import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  Wrench,
  Sparkles,
  Zap,
  ShieldCheck,
  Eye,
  EyeOff,
  LoaderCircle,
} from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({
    name: "",
    org_name: "",
    email: "",
    password: "",
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
const endpoint =
  mode === "login"
    ? "/auth/login"
    : "/auth/register";

      const payload =
        mode === "login"
          ? {
              email: form.email.trim(),
              password: form.password,
            }
          : {
              name: form.name.trim(),
              email: form.email.trim(),
              password: form.password,
              org_name: form.org_name.trim(),
            };

      const { data } = await api.post(endpoint, payload);

      if (rememberMe) {
        localStorage.setItem("mantix_remember_email", form.email.trim());
      } else {
        localStorage.removeItem("mantix_remember_email");
      }

      login(data.token, data.user);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode((current) => (current === "login" ? "register" : "login"));
    setError("");
    setShowPassword(false);
  };

  return (
    <main className="flex min-h-screen bg-[#F5F5F7]">
      {/* Panel de marca */}
      <section className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#08090B] p-12 text-white lg:flex">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-[#0066CC]/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 -left-20 h-96 w-96 rounded-full bg-[#5856D6]/20 blur-3xl" />

        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0066CC] shadow-[0_8px_24px_rgba(0,102,204,0.35)]">
            <Wrench className="h-6 w-6 text-white" strokeWidth={1.8} />
          </div>

          <span className="font-display text-2xl font-bold tracking-tight">
            Navirox
          </span>
        </div>

        <div className="relative max-w-xl">
          <p className="mb-5 text-sm font-semibold uppercase tracking-[0.18em] text-[#0A84FF]">
            Gestión inteligente de mantenimiento
          </p>

          <h1 className="font-display text-5xl font-bold leading-[1.08] tracking-tight">
            Gestiona tu empresa de mantenimiento sin caos.
          </h1>

          <p className="mt-6 max-w-lg text-lg leading-relaxed text-white/60">
            Avisos, técnicos, órdenes, clientes y operaciones en una sola
            plataforma rápida, clara e inteligente.
          </p>

          <div className="mt-10 flex flex-col gap-4">
            {[
              {
                icon: Sparkles,
                text: "IA integrada para presupuestos, resúmenes y priorización",
              },
              {
                icon: Zap,
                text: "Flujos rápidos que reducen tareas administrativas",
              },
              {
                icon: ShieldCheck,
                text: "Roles, permisos y datos separados por empresa",
              },
            ].map(({ icon: Icon, text }) => (
              <div
                key={text}
                className="flex items-center gap-3 text-sm text-white/80"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/5">
                  <Icon
                    className="h-5 w-5 text-[#0A84FF]"
                    strokeWidth={1.8}
                  />
                </div>

                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="relative text-sm text-white/40">
          © 2026 Navirox · Gestión inteligente de mantenimiento
        </p>
      </section>

      {/* Formulario */}
      <section className="flex w-full items-center justify-center px-6 py-10 lg:w-1/2 lg:px-12">
        <div className="w-full max-w-md">
          <div className="mb-10 lg:hidden">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0066CC]">
                <Wrench className="h-5 w-5 text-white" strokeWidth={1.8} />
              </div>

              <span className="font-display text-xl font-bold tracking-tight text-[#1D1D1F]">
                Navirox
              </span>
            </div>
          </div>

          <div className="rounded-[28px] border border-black/[0.05] bg-white p-7 shadow-[0_18px_60px_rgba(0,0,0,0.06)] sm:p-9">
            <h2 className="font-display text-3xl font-bold tracking-tight text-[#1D1D1F]">
              {mode === "login" ? "Accede a Navirox" : "Crea tu cuenta"}
            </h2>

            <p className="mt-2 text-sm leading-relaxed text-[#86868B]">
              {mode === "login"
                ? "Gestiona toda tu operación desde un único lugar."
                : "Crea tu empresa y empieza a organizar tus operaciones."}
            </p>

            <form onSubmit={submit} className="mt-8 space-y-5">
              {mode === "register" && (
                <>
                  <div>
                    <label
                      htmlFor="name"
                      className="mb-2 block text-sm font-medium text-[#1D1D1F]"
                    >
                      Nombre
                    </label>

                    <input
                      id="name"
                      data-testid="name-input"
                      value={form.name}
                      onChange={(event) =>
                        updateField("name", event.target.value)
                      }
                      required
                      autoComplete="name"
                      className="w-full rounded-2xl border border-[#D2D2D7] bg-[#F5F5F7] px-4 py-3.5 text-[#1D1D1F] outline-none transition-colors focus:border-[#0066CC] focus:bg-white focus:ring-4 focus:ring-[#0066CC]/10"
                      placeholder="Tu nombre"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="org_name"
                      className="mb-2 block text-sm font-medium text-[#1D1D1F]"
                    >
                      Nombre de la empresa
                    </label>

                    <input
                      id="org_name"
                      data-testid="org-input"
                      value={form.org_name}
                      onChange={(event) =>
                        updateField("org_name", event.target.value)
                      }
                      required
                      autoComplete="organization"
                      className="w-full rounded-2xl border border-[#D2D2D7] bg-[#F5F5F7] px-4 py-3.5 text-[#1D1D1F] outline-none transition-colors focus:border-[#0066CC] focus:bg-white focus:ring-4 focus:ring-[#0066CC]/10"
                      placeholder="Tu empresa S.L."
                    />
                  </div>
                </>
              )}

              <div>
                <label
                  htmlFor="email"
                  className="mb-2 block text-sm font-medium text-[#1D1D1F]"
                >
                  Email
                </label>

                <input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  value={form.email}
                  onChange={(event) =>
                    updateField("email", event.target.value)
                  }
                  required
                  autoComplete="email"
                  className="w-full rounded-2xl border border-[#D2D2D7] bg-[#F5F5F7] px-4 py-3.5 text-[#1D1D1F] outline-none transition-colors focus:border-[#0066CC] focus:bg-white focus:ring-4 focus:ring-[#0066CC]/10"
                  placeholder="tu@empresa.com"
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="mb-2 block text-sm font-medium text-[#1D1D1F]"
                >
                  Contraseña
                </label>

                <div className="relative">
                  <input
                    id="password"
                    data-testid="password-input"
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(event) =>
                      updateField("password", event.target.value)
                    }
                    required
                    minLength={6}
                    autoComplete={
                      mode === "login"
                        ? "current-password"
                        : "new-password"
                    }
                    className="w-full rounded-2xl border border-[#D2D2D7] bg-[#F5F5F7] px-4 py-3.5 pr-12 text-[#1D1D1F] outline-none transition-colors focus:border-[#0066CC] focus:bg-white focus:ring-4 focus:ring-[#0066CC]/10"
                    placeholder="••••••••"
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 rounded-lg p-1 text-[#86868B] transition-colors hover:bg-black/5 hover:text-[#1D1D1F] focus:outline-none focus:ring-2 focus:ring-[#0066CC]/30"
                    aria-label={
                      showPassword
                        ? "Ocultar contraseña"
                        : "Mostrar contraseña"
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" strokeWidth={1.8} />
                    ) : (
                      <Eye className="h-5 w-5" strokeWidth={1.8} />
                    )}
                  </button>
                </div>
              </div>

              {mode === "login" && (
                <div className="flex items-center justify-between gap-4 text-sm">
                  <label className="flex cursor-pointer items-center gap-2 text-[#6E6E73]">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(event) =>
                        setRememberMe(event.target.checked)
                      }
                      className="h-4 w-4 rounded border-[#D2D2D7] accent-[#0066CC]"
                    />
                    Recordarme
                  </label>

                  <button
                    type="button"
                    className="font-medium text-[#0066CC] transition-colors hover:text-[#004499] hover:underline"
                    onClick={() =>
                      setError(
                        "La recuperación de contraseña estará disponible próximamente."
                      )
                    }
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
              )}

              {error && (
                <div
                  data-testid="auth-error"
                  role="alert"
                  className="rounded-2xl border border-[#FF3B30]/15 bg-[#FF3B30]/5 px-4 py-3 text-sm text-[#D70015]"
                >
                  {error}
                </div>
              )}

              <button
                data-testid="submit-auth"
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#0066CC] px-6 py-3.5 font-medium text-white shadow-[0_8px_22px_rgba(0,102,204,0.28)] transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-[#004499] hover:shadow-[0_12px_28px_rgba(0,102,204,0.32)] focus:outline-none focus:ring-4 focus:ring-[#0066CC]/20 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {loading && (
                  <LoaderCircle className="h-5 w-5 animate-spin" />
                )}

                {loading
                  ? "Cargando..."
                  : mode === "login"
                    ? "Entrar a Navirox"
                    : "Crear cuenta"}
              </button>
            </form>

            <div className="mt-7 border-t border-[#E8E8ED] pt-6">
              <p className="text-center text-sm text-[#86868B]">
                {mode === "login"
                  ? "¿Tu empresa todavía no usa Navirox?"
                  : "¿Ya tienes una cuenta?"}{" "}
                <button
                  data-testid="toggle-mode"
                  type="button"
                  onClick={toggleMode}
                  className="font-medium text-[#0066CC] transition-colors hover:text-[#004499] hover:underline"
                >
                  {mode === "login" ? "Crear cuenta" : "Iniciar sesión"}
                </button>
              </p>
            </div>
          </div>

          <p className="mt-5 text-center text-xs text-[#86868B]">
            Al continuar, aceptas los términos de uso y la política de
            privacidad de Navirox.
          </p>
        </div>
      </section>
    </main>
  );
}